import React from 'react';
import {
  Box,
  Tab,
  Tabs,
  Chip,
  Typography,
  Skeleton,
  useTheme,
} from '@mui/material';
import { DetailPanel } from 'components/shared/DetailPanel';
import { DetailPanelHeader } from 'components/shared/DetailPanelHeader';
import GeneralInfoTab, { GeneralInfoTabSkeleton } from './tabs/GeneralInfoTab';
import CasesTab from './tabs/CasesTab';
import ContactsTab from './tabs/ContactsTab';
import { Customer, CustomerType } from 'containers/pages/customers/types';

interface CustomerDetailPanelProps {
  open: boolean;
  onClose: () => void;
  customer: Customer | null;
  loading: boolean;
  error: string | null;
  activeTab: number;
  onTabChange: (tab: number) => void;
  onRefetch: () => void;
  customerTypes: CustomerType[];
  onSave: (formData: Record<string, unknown>, isEdit: boolean) => Promise<void>;
}

function LoadingSkeleton() {
  return (
    <>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Skeleton variant="text" width="60%" height={32} />
        <Skeleton variant="text" width="40%" height={20} sx={{ mt: 0.5 }} />
        <Skeleton variant="rounded" width={80} height={24} sx={{ mt: 1 }} />
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Skeleton width={80} height={40} />
          <Skeleton width={50} height={40} />
          <Skeleton width={70} height={40} />
        </Box>
      </Box>
      <GeneralInfoTabSkeleton />
    </>
  );
}

export default function CustomerDetailPanel({
  open,
  onClose,
  customer,
  loading,
  error,
  activeTab,
  onTabChange,
  onRefetch,
  customerTypes,
  onSave,
}: CustomerDetailPanelProps) {
  const theme = useTheme();

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    onTabChange(newValue);
  };

  const renderContent = () => {
    if (loading) {
      return <LoadingSkeleton />;
    }

    if (error) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error" variant="body1" sx={{ mb: 1 }}>
            {error}
          </Typography>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={onRefetch}
          >
            Try again
          </Typography>
        </Box>
      );
    }

    if (!customer) return null;

    return (
      <>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Customer detail tabs"
            sx={{
              px: 2,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 48,
                fontSize: '0.875rem',
              },
            }}
          >
            <Tab label="General Info" id="customer-tab-0" aria-controls="customer-tabpanel-0" />
            <Tab label="Cases" id="customer-tab-1" aria-controls="customer-tabpanel-1" />
            <Tab label="Contacts" id="customer-tab-2" aria-controls="customer-tabpanel-2" />
          </Tabs>
        </Box>

        <Box
          role="tabpanel"
          id={`customer-tabpanel-${activeTab}`}
          aria-labelledby={`customer-tab-${activeTab}`}
        >
          {activeTab === 0 && (
            <GeneralInfoTab
              customer={customer}
              customerTypes={customerTypes}
              onSave={onSave}
              onSaveSuccess={onRefetch}
            />
          )}
          {activeTab === 1 && <CasesTab customerId={customer.Id} />}
          {activeTab === 2 && <ContactsTab customerId={customer.Id} />}
        </Box>
      </>
    );
  };

  return (
    <DetailPanel
      open={open}
      onClose={onClose}
      maxWidth="md"
      header={
        !loading && customer ? (
          <DetailPanelHeader
            title={customer.Name}
            subtitle={customer.Address || undefined}
            backgroundColor={theme.palette.primary.main}
            onClose={onClose}
            chips={
              customer.TypeTitle ? (
                <Chip
                  label={customer.TypeTitle}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              ) : undefined
            }
          />
        ) : undefined
      }
    >
      {renderContent()}
    </DetailPanel>
  );
}
